export interface TranscriptWord {
  word: string;
  start: number; // seconds
  end: number; // seconds
  speaker: number; // 0 or 1
  confidence?: number;
  punctuated_word?: string;
}

export interface TranscriptSegment {
  speaker: string;
  start: number;
  end: number;
  words?: TranscriptWord[];
  transcript?: string;
}

export interface ConversationAnalytics {
  talkListenRatio: { talk: number; listen: number }; // percentages, sum ~100
  fillerWordsPerMin: number;
  longestMonologueSeconds: number;
  interruptions: number;
  paceWordsPerMin: number;
  energyLevel: 'Low' | 'Moderate' | 'High';
  totalDurationSeconds: number;
}

const FILLER_WORDS = new Set([
  'um',
  'umm',
  'uh',
  'uhh',
  'like',
  'actually',
  'basically',
  'literally',
]);

/**
 * Computes conversation analytics from transcript word-level data.
 *
 * @param transcriptJson - array of transcript segments (each with a `words` array)
 * @param repSpeakerId - numeric speaker id representing the sales rep (VERIFY per data — not guaranteed to always be 0)
 * @param apiInterruptionsCount - `interruptionsCount` from API (pass-through, not recalculated)
 */
export function computeConversationAnalytics(
  transcriptJson: TranscriptSegment[] | undefined | null,
  repSpeakerId: number = 0,
  apiInterruptionsCount: number = 0,
): ConversationAnalytics | null {
  if (!transcriptJson?.length) return null;

  const words: TranscriptWord[] = transcriptJson
    .flatMap((seg) => seg.words || [])
    .filter((w) => typeof w.start === 'number' && typeof w.end === 'number')
    .sort((a, b) => a.start - b.start);

  if (!words.length) return null;

  const totalDurationSeconds = words[words.length - 1].end - words[0].start;
  if (totalDurationSeconds <= 0) return null;

  // ---- Talk / Listen ratio (active speaking time per speaker) ----
  let repSpeakingSeconds = 0;
  let otherSpeakingSeconds = 0;
  let repWordCount = 0;

  for (const w of words) {
    const dur = Math.max(w.end - w.start, 0);
    if (w.speaker === repSpeakerId) {
      repSpeakingSeconds += dur;
      repWordCount += 1;
    } else {
      otherSpeakingSeconds += dur;
    }
  }

  const totalSpeakingSeconds = repSpeakingSeconds + otherSpeakingSeconds;
  const talkPct =
    totalSpeakingSeconds > 0 ? Math.round((repSpeakingSeconds / totalSpeakingSeconds) * 100) : 0;
  const listenPct = 100 - talkPct;

  // ---- Pace (rep's words/min, based on rep's own active speaking time) ----
  const repMinutes = repSpeakingSeconds / 60;
  const paceWordsPerMin = repMinutes > 0 ? Math.round(repWordCount / repMinutes) : 0;

  // ---- Filler words per minute (rep only) ----
  let fillerCount = 0;
  for (const w of words) {
    if (w.speaker !== repSpeakerId) continue;
    const token = (w.word || w.punctuated_word || '').toLowerCase().replace(/[^a-z']/g, '');
    if (FILLER_WORDS.has(token)) fillerCount += 1;
  }
  const fillerWordsPerMin = repMinutes > 0 ? Math.round((fillerCount / repMinutes) * 10) / 10 : 0;

  // ---- Longest monologue (longest unbroken run by one speaker) ----
  let longestMonologueSeconds = 0;
  let runStart = words[0].start;
  let runSpeaker = words[0].speaker;
  let prevEnd = words[0].end;

  for (let i = 1; i < words.length; i++) {
    const w = words[i];
    if (w.speaker === runSpeaker) {
      prevEnd = w.end;
      continue;
    }
    const runDuration = prevEnd - runStart;
    if (runDuration > longestMonologueSeconds) longestMonologueSeconds = runDuration;
    runStart = w.start;
    runSpeaker = w.speaker;
    prevEnd = w.end;
  }
  const finalRunDuration = prevEnd - runStart;
  if (finalRunDuration > longestMonologueSeconds) longestMonologueSeconds = finalRunDuration;
  longestMonologueSeconds = Math.round(longestMonologueSeconds);

  // ---- Energy level (approximated — no raw audio signal available) ----
  // Proxy: faster pace + less "dead air" (gaps between words) ≈ higher energy.
  // This is a text-only heuristic, NOT actual pitch/volume analysis.
  let totalGapSeconds = 0;
  for (let i = 1; i < words.length; i++) {
    const gap = words[i].start - words[i - 1].end;
    if (gap > 0) totalGapSeconds += gap;
  }
  const deadAirRatio = totalGapSeconds / totalDurationSeconds;

  let energyLevel: ConversationAnalytics['energyLevel'] = 'Moderate';
  if (paceWordsPerMin >= 130 && deadAirRatio < 0.15) {
    energyLevel = 'High';
  } else if (paceWordsPerMin < 100 || deadAirRatio > 0.3) {
    energyLevel = 'Low';
  }

  return {
    talkListenRatio: { talk: talkPct, listen: listenPct },
    fillerWordsPerMin,
    longestMonologueSeconds,
    interruptions: apiInterruptionsCount,
    paceWordsPerMin,
    energyLevel,
    totalDurationSeconds: Math.round(totalDurationSeconds),
  };
}

// Raw shape as returned directly by the API's own `conversationAnalytics` field
// (pre-aggregated on backend — used when word-level transcriptJson isn't available)
export interface ApiConversationAnalytics {
  energy_level?: string;
  interruptions?: number;
  talk_listen_ratio?: { talk: number; total?: number; listen?: number };
  pace_words_per_min?: number;
  filler_words_per_min?: number;
  longest_monologue_seconds?: number;
  total_duration_seconds?: number;
}

/**
 * Maps API's precomputed `conversationAnalytics` into the same shape
 * the UI already expects (ConversationAnalytics).
 */
export function mapApiConversationAnalytics(
  api: ApiConversationAnalytics | undefined | null,
): ConversationAnalytics | null {
  if (!api) return null;

  const talk = api.talk_listen_ratio?.talk;
  if (typeof talk !== 'number') return null;

  const total = api.talk_listen_ratio?.total ?? 0; // NEW — raw total seconds
  const listen =
    typeof api.talk_listen_ratio?.listen === 'number'
      ? api.talk_listen_ratio.listen
      : Math.max(0, total - talk); // FIXED — total se subtract karo, 100 se nahi

  const rawEnergy = (api.energy_level || '').toLowerCase();
  const energyLevel: ConversationAnalytics['energyLevel'] =
    rawEnergy === 'high' ? 'High' : rawEnergy === 'low' ? 'Low' : 'Moderate';

  return {
    talkListenRatio: { talk, listen },
    fillerWordsPerMin: api.filler_words_per_min ?? 0,
    longestMonologueSeconds: api.longest_monologue_seconds ?? 0,
    interruptions: api.interruptions ?? 0,
    paceWordsPerMin: api.pace_words_per_min ?? 0,
    energyLevel,
    totalDurationSeconds: api.total_duration_seconds ?? total, // NEW fallback
  };
}

export function formatMonologueDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}
